"use client";

import Image from "next/image";
import { useState, useEffect } from "react";
import { motion, AnimatePresence, type Transition } from "framer-motion";
import { SHOW_ACADEMY } from "@/constants/feature-flags";
import type { AcademySectionQueryResult } from "@/sanity/schemas/lib/queries";

interface AcademySectionProps {
    sanityContent: AcademySectionQueryResult;
}

const slideVariants = {
    initial: { y: -20, opacity: 0, zIndex: 10 },
    animate: { y: 0, opacity: 1, zIndex: 10 },
    exit: { y: 0, opacity: 0, zIndex: 0 },
};

export function AcademySection({ sanityContent }: AcademySectionProps) {
    const slides =
        sanityContent?.trainings?.map((training, index) => ({
            id: index + 1,
            title: training.title,
            description: training.description,
            image: training.img,
            alt: training.altImg || training.title,
        })) || [];

    const [absoluteIndex, setAbsoluteIndex] = useState(0);

    useEffect(() => {
        if (slides.length <= 1) return;

        const timer = setInterval(() => {
            setAbsoluteIndex((prev) => prev + 1);
        }, 8000);
        return () => clearInterval(timer);
    }, [slides.length]);

    if (!sanityContent) return null;

    const currentIndex = slides.length > 0 ? absoluteIndex % slides.length : 0;
    const cycleCount = slides.length > 0 ? Math.floor(absoluteIndex / slides.length) : 0;
    const currentSlide = slides[currentIndex];

    const indicatorTransition: Transition = {
        duration: 1,
        ease: "easeInOut",
    };

    return (
        <section className="bg-light pt-8 pb-14 md:py-25">
            <div className="mx-auto max-w-7xl px-5 md:px-20 flex flex-col items-center gap-10 md:gap-35">
                <div className="flex flex-col items-center gap-10 text-center">
                    <div className="flex flex-col items-center gap-4">
                        <h2 className="text-h2-mobile font-semibold leading-120 text-ink md:text-h2-desktop">
                            {sanityContent.title?.prefix}{" "}
                            <span className="bg-linear-to-r from-ink via-system-blue to-ink bg-clip-text text-transparent">
                                {sanityContent.title?.highlight}
                            </span>
                        </h2>
                        <p className="text-subtitle leading-140 text-system-subtitle max-w-132.5">
                            {sanityContent.subtitle}
                        </p>
                    </div>
                    {SHOW_ACADEMY && (
                        <a
                            href={sanityContent.buttonHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center rounded-lg border border-system-glow/60 px-6 py-3 text-base font-medium text-ink backdrop-blur-sm"
                        >
                            {sanityContent.button}
                        </a>
                    )}
                </div>

                {slides.length > 0 && currentSlide && (
                    <div className="flex w-full max-w-274.5 flex-col items-center gap-18">
                        <div className="flex w-full flex-col items-center gap-16 md:flex-row md:gap-37">
                            <div className="relative flex w-full flex-col md:w-md shrink-0">
                                <div className="invisible pointer-events-none grid">
                                    {slides.map((slide) => (
                                        <div
                                            key={slide.id}
                                            className="col-start-1 row-start-1 flex flex-col gap-3"
                                        >
                                            <h3 className="text-h3-desktop font-semibold leading-120 text-ink">
                                                {slide.title}
                                            </h3>
                                            <p className="text-base leading-normal text-system-subtitle">
                                                {slide.description}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                <AnimatePresence initial={false}>
                                    <motion.div
                                        key={`text-${currentSlide.id}`}
                                        variants={slideVariants}
                                        initial="initial"
                                        animate="animate"
                                        exit="exit"
                                        transition={{ duration: 1, ease: "easeInOut" }}
                                        className="absolute inset-0 flex flex-col gap-3"
                                    >
                                        <h3 className="text-h3-desktop font-semibold leading-120 text-ink">
                                            {currentSlide.title}
                                        </h3>
                                        <p className="text-base leading-normal text-system-subtitle">
                                            {currentSlide.description}
                                        </p>
                                    </motion.div>
                                </AnimatePresence>
                            </div>

                            <div className="relative h-77 w-full shrink-0 md:w-125.5">
                                <AnimatePresence initial={false}>
                                    <motion.div
                                        key={`image-${currentSlide.id}`}
                                        variants={slideVariants}
                                        initial="initial"
                                        animate="animate"
                                        exit="exit"
                                        transition={{ duration: 1, ease: "easeInOut" }}
                                        className="absolute inset-0"
                                    >
                                        <Image
                                            src={currentSlide.image}
                                            alt={currentSlide.alt}
                                            fill
                                            quality={100}
                                            className="rounded-3xl object-cover shadow-[0px_4px_36px_0px_rgba(0,0,0,0.15)]"
                                            sizes="(min-width: 768px) 502px, 100vw"
                                        />
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>

                        <div className="relative h-2 w-full max-w-63 shrink-0">
                            <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-[repeating-linear-gradient(to_right,#c4c4c4_0px,#c4c4c4_2px,transparent_2px,transparent_6px)]" />

                            <div
                                className={`absolute top-1/2 z-10 -translate-y-1/2 rounded-full transition-colors duration-500 ${currentIndex === 0 ? "size-2 bg-yellow-2" : "size-1.5 bg-[#c4c4c4]"}`}
                                style={{ left: currentIndex === 0 ? "-4px" : "-3px" }}
                            />

                            <div className="absolute inset-x-0 top-0 bottom-0 overflow-hidden">
                                <AnimatePresence initial={false}>
                                    <motion.div
                                        key={`indicator-cycle-${cycleCount}`}
                                        className="absolute top-1/2 h-[2px] -translate-y-1/2 bg-yellow-2"
                                        initial={{ left: "-33.33%" }}
                                        animate={{
                                            left: `${(currentIndex / slides.length) * 100}%`,
                                        }}
                                        exit={{ left: "100%" }}
                                        transition={indicatorTransition}
                                        style={{ width: `${(1 / slides.length) * 100}%` }}
                                    />
                                </AnimatePresence>
                            </div>

                            <div
                                className={`absolute top-1/2 z-10 -translate-y-1/2 rounded-full transition-colors duration-500 ${currentIndex === slides.length - 1 ? "size-2 bg-yellow-2" : "size-1.5 bg-[#c4c4c4]"}`}
                                style={{
                                    right: currentIndex === slides.length - 1 ? "-4px" : "-3px",
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
